import { Link } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { PageHeader } from '../components/common/PageHeader';
import { Reveal } from '../components/animation/Reveal';
import { Icon } from '../components/common/Icon';
import { Eyebrow } from '../components/common/Eyebrow';
import { knowledgeArticles, type KnowledgeArticle } from '../data/knowledge';
import { breadcrumbSchema, collectionPageSchema, itemListSchema } from '../lib/seo';

const SECTION_ICON: Record<KnowledgeArticle['section'], string> = {
  'Soil biology': 'Microscope',
  'Bio-inputs': 'FlaskConical',
  'Application practice': 'Droplets',
};

const SECTION_ORDER: KnowledgeArticle['section'][] = ['Soil biology', 'Bio-inputs', 'Application practice'];

/**
 * The knowledge index. Grouped by subject rather than listed by date, because nothing here is news —
 * these are reference explanations, and a reader arrives looking for a subject.
 */
export default function KnowledgePage() {
  const crumbs = [
    { name: 'Home', path: '/' },
    { name: 'Knowledge', path: '/knowledge' },
  ];

  return (
    <>
      <Seo
        title="Soil Biology & Bio-Input Knowledge Base"
        description="Plain explanations of the science behind bio-inputs — soil biology, the rhizosphere, bio-fertilizers, biostimulants, humic and fulvic substances, amino acids, and how application method changes what an input can do."
        path="/knowledge"
        jsonLd={[
          collectionPageSchema({
            name: 'GreenbayBio knowledge base',
            description:
              'Reference explanations of soil biology and agricultural bio-inputs, written to explain the science independently of any product claim.',
            path: '/knowledge',
          }),
          itemListSchema(
            'Knowledge articles',
            '/knowledge',
            knowledgeArticles.map((a) => ({ name: a.title, path: `/knowledge/${a.slug}` })),
          ),
          breadcrumbSchema(crumbs),
        ]}
      />
      <PageHeader
        eyebrow="Knowledge base"
        title="The science, explained before the product."
        subtitle="What soil biology, bio-fertilizers and biostimulants actually are and how they work — written to be useful whether or not you ever buy anything from us. Product-specific facts live on each product page."
      />

      <div className="container-x py-14">
        {SECTION_ORDER.map((section) => {
          const items = knowledgeArticles.filter((a) => a.section === section);
          if (!items.length) return null;
          return (
            <section key={section} className="mb-14 last:mb-0" aria-labelledby={`sec-${section}`}>
              <Eyebrow>
                <span id={`sec-${section}`}>{section}</span>
              </Eyebrow>
              <Reveal className="mt-5 grid gap-5 md:grid-cols-2" stagger={0.08} y={22}>
                {items.map((a) => (
                  <article key={a.slug} className="rounded-xl border border-sand-200 bg-white p-7 shadow-soft">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                      <Icon name={SECTION_ICON[a.section]} className="h-5 w-5" />
                    </span>
                    <h2 className="mt-5 font-display text-xl font-semibold text-ink">
                      <Link to={`/knowledge/${a.slug}`} className="hover:text-brand-700">
                        {a.title}
                      </Link>
                    </h2>
                    <p className="mt-2 leading-relaxed text-ink-500">{a.summary}</p>
                    <Link
                      to={`/knowledge/${a.slug}`}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:underline"
                    >
                      Read {a.title.replace(/\?$/, '').toLowerCase()}
                      <Icon name="ArrowRight" className="h-4 w-4" />
                    </Link>
                  </article>
                ))}
              </Reveal>
            </section>
          );
        })}

        <div className="rounded-xl border border-sand-200 bg-sand-100/50 p-8">
          <h2 className="font-display text-h3 font-semibold text-ink">Looking for a product instead?</h2>
          <p className="mt-2 max-w-2xl text-ink-500">
            Composition, specifications, crop-by-crop doses, approvals and data sheets are on each product
            page. Crop programmes are grouped by crop.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/catalog" className="btn btn-primary btn-md">
              All products
            </Link>
            <Link to="/crop-solutions" className="btn btn-outline btn-md">
              Crop programmes
            </Link>
            <Link to="/resources" className="btn btn-outline btn-md">
              Usage guides &amp; documents
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
